import React, {useState, useEffect, useRef, useContext} from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  View,
  Keyboard,
  PermissionsAndroid,
  Linking,
} from 'react-native';
import {SafeAreaView, useSafeAreaInsets} from 'react-native-safe-area-context';
import {launchCamera, launchImageLibrary} from 'react-native-image-picker';
import ActionSheet from '@alessiocancian/react-native-actionsheet';
import RNExitApp from 'react-native-exit-app';
import * as Keychain from 'react-native-keychain';

import Button from '../../components/elements/Button';
import Header from '../../components/elements/Header';
import Text from '../../components/elements/Text';
import Popup from '../../components/elements/Popup';
import ContactDetails from '../../components/modules/ContactDetails';
import PersonalDetails from '../../components/modules/PersonalDetails';

import {
  BUTTON_TYPES,
  DIABETES_TYPES,
  GENDER_OPTIONS,
  KEYS,
  MAX_FILE_UPLOAD_SIZE,
} from '../../utils/Constants';
import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../theme';
import {
  DataURIToBlob,
  DLog,
  isEmpty,
  removeAllLocalData,
  validName,
} from '../../utils/HelperMethods';
import UserAvatar from '../../components/elements/UserAvatar';
import {
  getUserProfileData,
  removeData,
  storeUserProfileData,
  getData,
  storeData,
} from '../../utils/GeneralStorage';
import {
  DELETE_USER,
  HTTP_METHODS,
  UPDATE_USER,
  VIEW_DETAILS,
} from '../../utils/URLConstants';
import {fetchApi, formDataRequest, parseError} from '../../utils/ApiHelper';
import {loadingPauseContext} from '../../contexts/LoadingPauseContext';
import {errorStatusContext} from '../../contexts/ErrorStatusContext';
import {tID} from '../../utils/TestIdHelper';
import BottomExpandableDrawer from '../../components/modules/BottomExpandableDrawer';
import {CloseIcon, LeftArrowIcon} from '../../utils/SvgIcons';
import {shopContext} from '../../contexts/ShopContext';

const PermissionDeniedModal = ({
  visible = false,
  onRetry = () => {},
  onClose = () => {},
}) => {
  return (
    <BottomExpandableDrawer
      title={'Permission Denied'}
      visible={visible}
      subtitle={'You may still grant permission'}
      buttons={[
        {
          title: 'Close',
          buttonType: BUTTON_TYPES.Bordered,
          onPress: onClose,
        },
        {
          title: 'Retry',
          buttonType: BUTTON_TYPES.Filled,
          onPress: onRetry,
        },
      ]}
    />
  );
};

const PermissionBlockedModal = ({
  visible = false,
  onOpen = () => {},
  onClose = () => {},
}) => {
  return (
    <BottomExpandableDrawer
      title={'Permission Blocked'}
      visible={visible}
      subtitle={'Please enable permission in your app settings'}
      buttons={[
        {
          title: 'Close',
          buttonType: BUTTON_TYPES.Bordered,
          onPress: onClose,
        },
        {
          title: 'Open Settings',
          buttonType: BUTTON_TYPES.Filled,
          onPress: onOpen,
        },
      ]}
    />
  );
};

const EditProfile = ({
  children,
  navigation,
  onDobChange = () => {},
  dob,
  onClose = () => {},
  onOpenCameraClick = () => {},
  imageUri,
  setShowChangePasswordPopup = () => {},
  setShowChangePhoneNumberPopup = () => {},
  showVerifyPhoneNumberPopup,
}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  const [profileImage, setProfileImage] = useState();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [gender, setGender] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [diabetesTypes, setDiabetesTypes] = useState([]);
  const [otherDiabetesType, setOtherDiabetesType] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState(null);
  const [timeFormat, setTimeFormat] = useState('24');
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [otherTypeError, setOtherTypeError] = useState(false);
  const [isValidForm, setIsValidForm] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [showSuccesModal, setShowSuccesModal] = useState(false);
  const [cameraPermissionDenied, setCameraPermissionDenied] = useState(false);
  const [cameraPermissionBlocked, setCameraPermissionBlocked] = useState(false);
  const insets = useSafeAreaInsets();

  const actionsheet = useRef(null);
  const selectedDiabetesTypes = useRef([]);
  const diabetesTypesData = useRef([]);

  const lpc = useContext(loadingPauseContext);
  const esc = useContext(errorStatusContext);
  const ShopC = useContext(shopContext);

  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- validation intentionally keyed to form fields only
  }, [firstName, lastName, diabetesTypes, otherDiabetesType]);

  useEffect(() => {
    setIsValidForm(true);
  }, [gender, dateOfBirth, timeFormat]);

  useEffect(() => {
    let unsubscribe;
    if (Platform.OS !== 'web') {
      navigation.addListener('focus', () => {
        setTimeout(() => {
          getProfile();
        }, 800);
      });
    }
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only focus listener / data load
  }, []);

  useEffect(() => {
    if (Platform.OS === 'web' && !showVerifyPhoneNumberPopup) {
      getProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch intentionally keyed to popup close only
  }, [showVerifyPhoneNumberPopup]);

  useEffect(() => {
    if (dob !== dateOfBirth && Platform.OS === 'web') setDateOfBirth(dob);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-way prop→state sync on dob change only; adding dateOfBirth would revert local edits
  }, [dob]);

  useEffect(() => {
    if (imageUri) {
      setProfileImage({uri: imageUri});
      setIsValidForm(true);
    }
  }, [imageUri]);

  const onDateOfBirthChange = value => {
    onDobChange(value);
    setDateOfBirth(value);
  };

  const updateProfileData = async profile => {
    const localProfile = await getUserProfileData();
    storeUserProfileData({...localProfile, ...profile});
  };

  const getProfile = () => {
    lpc.setVisible(true);
    fetchApi(HTTP_METHODS.GET, VIEW_DETAILS)
      .then(res => res.data)
      .then(response => {
        lpc.setVisible(false);
        const profile = response.data.data;
        const types = (profile.patientCondition || []).map(item => {
          let type = '';

          Object.keys(DIABETES_TYPES).every(i => {
            if (String(DIABETES_TYPES[i]) == String(item.condition?.id)) {
              type = i;
              return false;
            } else if (item.condition?.id > 5) {
              type = 'Other';
              setOtherDiabetesType(item.condition?.description);
              return false;
            }
            return true;
          });
          return type;
        });
        setFirstName(profile.firstname);
        setLastName(profile.lastname);
        setPhone(profile.phone);
        setEmail(profile.email);
        setDiabetesTypes(types);
        let gender = '';

        Object.keys(GENDER_OPTIONS).every(i => {
          if (String(GENDER_OPTIONS[i]) == String(profile.gender)) {
            gender = i;
            return false;
          }
          return true;
        });
        setGender(gender);
        if (profile.dateOfBirth) {
          onDateOfBirthChange(new Date(profile.dateOfBirth));
        }
        selectedDiabetesTypes.current = types;
        diabetesTypesData.current = profile.patientCondition;
        updateProfileData(profile);
        setProfileImage(profile.signedImageUrl);
        setIsValidForm(false);

        // Load time format preference from local storage
        getData(KEYS.timeFormat).then(format => {
          if (format) {
            setTimeFormat(format);
          }
        });
      })
      .catch(error => {
        // Dlog(error);
        lpc.setVisible(false);
        esc.showError(error);
      });
  };

  const validateForm = () => {
    if (isEmpty(firstName) || isEmpty(lastName)) {
      setIsValidForm(false);
      return;
    }

    if (diabetesTypes.includes('Other') && isEmpty(otherDiabetesType)) {
      setIsValidForm(false);
      return;
    }
    setIsValidForm(true);
  };

  const updateProfile = async () => {
    if (firstName && !validName(firstName)) {
      setFirstNameError(true);
      return;
    } else {
      setFirstNameError(false);
    }

    if (lastName && !validName(lastName)) {
      setLastNameError(true);
      return;
    } else {
      setLastNameError(false);
    }

    Keyboard.dismiss();

    lpc.setVisible(true);
    const oldTypes = diabetesTypes;
    const newTypes = selectedDiabetesTypes.current;

    const added = oldTypes.filter(x => !newTypes.includes(x));
    const deleted = newTypes.filter(x => !oldTypes.includes(x));
    const params = new FormData();
    params.append('firstname', firstName);
    params.append('lastname', lastName);
    params.append('gender', parseInt(GENDER_OPTIONS[gender]));
    if (dateOfBirth) {
      params.append('dateOfBirth', dateOfBirth.toISOString());
    }
    if (profileImage && typeof profileImage != 'string') {
      if (Platform.OS === 'web') {
        const file = DataURIToBlob(profileImage.uri);
        try {
          if (file.size > MAX_FILE_UPLOAD_SIZE) {
            throw 'Image size should be less than 5 MB';
          } else {
            params.append('imageFile', file, 'imageFile.jpg');
          }
        } catch (e) {
          lpc.setVisible(false);
          esc.showError(e);
        }
      } else {
        try {
          if (profileImage.fileSize > MAX_FILE_UPLOAD_SIZE) {
            throw 'Image size should be less than 5 MB';
          } else {
            params.append('imageFile', {
              uri: profileImage.uri,
              name: 'imageFile.jpg',
              type: 'image/jpeg',
            });
          }
        } catch (e) {
          lpc.setVisible(false);
          esc.showError(e);
        }
      }
    }

    added.forEach(item => {
      if (item == 'Other') {
        params.append(
          'otherType',
          JSON.stringify({
            name: 'Diabetes',
            subtype: 'Other',
            description: otherDiabetesType,
          }),
        );
      } else {
        params.append('addConditions[]', DIABETES_TYPES[item]);
      }
    });

    deleted.forEach(item => {
      if (item == 'Other') {
        diabetesTypesData.current.every(i => {
          if (i.condition.id > 5) {
            params.append('deleteConditions[]', i.condition.id);
            return false;
          }
          return true;
        });
      } else {
        params.append('deleteConditions[]', DIABETES_TYPES[item]);
      }
    });

    // Dlog(params);

    // Save time format preference to local storage
    await storeData(KEYS.timeFormat, timeFormat);

    formDataRequest(HTTP_METHODS.PUT, UPDATE_USER, params)
      .then(async response => {
        // Check if response is ok (status 200-299)
        if (!response.ok) {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            throw new Error(errorData.message || errorData.error || `Server error: ${response.status}`);
          } else {
            // Server returned HTML or other non-JSON response
            throw new Error(`Server error: ${response.status}. Please try again or contact support.`);
          }
        }
        return response.json();
      })
      .then(res => {
        // Dlog('====> UPDATE', res);
        if (res.status === 'success') {
          getProfile();
        } else if (res.errors) {
          lpc.setVisible(false);
          esc.showError(res.errors);
        } else {
          lpc.setVisible(false);
        }
      })
      .catch(e => {
        // Dlog(e);
        let err = e.message || parseError(e);
        lpc.setVisible(false);
        esc.showError(err);
      });
  };

  const deleteAccout = () => {
    lpc.setVisible(true);
    fetchApi(HTTP_METHODS.DELETE, DELETE_USER)
      .then(res => res.data)
      .then(response => {
        // Dlog(response);
        lpc.setVisible(false);
        if (response.status == 'success') showDeleteSuccessPopup();
      })
      .catch(error => {
        // Dlog(error);
        lpc.setVisible(false);
        esc.showError(error);
      });
  };

  async function showDeleteSuccessPopup() {
    await removeAllData();
    setTimeout(() => {
      setShowSuccesModal(true);
    }, 800);
  }

  const removeAllData = async () => {
    if (Platform.OS !== 'web') await Keychain.resetGenericPassword();
    await removeData(KEYS.faceIdEnabled);
    await removeAllLocalData(ShopC);
  };

  const onPressUpdateAvtar = () => {
    actionsheet.current.show();
  };

  const onPermissionRetry = () => {
    setCameraPermissionDenied(false);
    requestCameraPermission();
  };

  const onOpenSettings = () => {
    setCameraPermissionBlocked(false);
    Linking.openSettings();
  };

  const requestCameraPermission = async () => {
    try {
      const granted = await PermissionsAndroid.request(
        PermissionsAndroid.PERMISSIONS.CAMERA,
      );
      if (granted === PermissionsAndroid.RESULTS.GRANTED) {
        openCamera();
      } else if (granted === PermissionsAndroid.RESULTS.DENIED) {
        setCameraPermissionDenied(true);
      } else {
        setCameraPermissionBlocked(true);
      }
    } catch (err) {
      console.warn(err);
    }
  };

  function openCamera() {
    launchCamera(pictureOptions)
      .then(res => {
        if (!res.didCancel && res?.assets) {
          setProfileImage(res.assets[0]);
          setIsValidForm(true);
        }
      })
      .catch(e => {
        // Dlog(e);
      });
  }

  const pictureOptions = {
    mediaType: 'photo',
    quality: 0.5,
  };

  const onPressActionSheetOption = index => {
    if (index == 0) {
      launchImageLibrary(pictureOptions)
        .then(res => {
          // Dlog(res?.assets);
          if (!res.didCancel && res?.assets) {
            setProfileImage(res.assets[0]);
            setIsValidForm(true);
          }
        })
        .catch(e => {
          // Dlog(e);
        });
    } else if (index == 1) {
      if (Platform.OS === 'web') {
        onOpenCameraClick();
      } else {
        if (Platform.OS === 'android') {
          requestCameraPermission();
        } else {
          openCamera();
        }
      }
    }
  };

  const onPressChangePassword = () => {
    if (Platform.OS === 'web') {
      setShowChangePasswordPopup(true);
    } else {
      navigation.navigate('ChangePassword');
    }
  };

  const onPressChangePhone = () => {
    if (Platform.OS === 'web') {
      setShowChangePhoneNumberPopup(true);
    } else {
      navigation.navigate('ChangePhoneNumber');
    }
  };

  const onPressDelete = () => {
    setShowDeletePopup(false);
    setTimeout(() => {
      deleteAccout();
    }, 500);
  };

  const renderDeleteAccountModal = () => {
    return (
      <Popup
        testID={tID('EditProfile-Popup-DeleteConfirmation')}
        title={'Delete Account'}
        visible={showDeletePopup}
        description="Are you sure you want to delete your account?">
        <Button
          testID={tID('EditProfile-Button-Cancel')}
          style={styles.contentContainer}
          onPress={() => setShowDeletePopup(false)}
          title={'Cancel'}
        />
        <Button
          testID={tID('EditProfile-Button-Delete')}
          style={styles.rightButton}
          onPress={onPressDelete}
          title={'Delete'}
          buttonType={BUTTON_TYPES.Filled}
          titleColor={colors.textOnAccent}
        />
      </Popup>
    );
  };

  const renderDeleteAccountConfirmation = () => {
    return (
      <Popup
        testID={tID('EditProfile-Popup-DeleteSuccess')}
        visible={showSuccesModal}
        textAlign="center"
        title="Success!"
        description={'Your account has been deleted.'}>
        <Button
          testID={tID('EditProfile-Button-Exit')}
          style={styles.contentContainer}
          buttonType={BUTTON_TYPES.Filled}
          titleColor={colors.textOnAccent}
          onPress={() => {
            if (Platform.OS === 'web') {
              navigation('/login');
            } else {
              RNExitApp.exitApp();
            }
          }}
          title={'Exit'}
        />
      </Popup>
    );
  };

  return (
    <View
      testID={tID('EditProfile-View-Main')}
      style={[
        styles.container,
        Platform.OS === 'web' && styles.fullScreenHeight,
      ]}>
      <View
        testID={tID('EditProfile-View-HeaderContainer')}
        style={[styles.headerView, {paddingTop: insets.top + 16}]}>
        <Header
          title="Edit Profile"
          onPressLeftIcon={
            Platform.OS === 'web' ? () => onClose() : navigation.goBack
          }
          leftImage={Platform.OS === 'web' ? <CloseIcon /> : <LeftArrowIcon />}
        />
      </View>
      <View style={styles.whiteBackgroundView} />
      <KeyboardAvoidingView
        testID={tID('EditProfile-KeyboardAvoidingView-1')}
        style={styles.contentContainer}
        behavior={Platform.OS == 'ios' ? 'padding' : null}>
        <ScrollView keyboardShouldPersistTaps="handled"
          testID={tID('EditProfile-ScrollView-1')}
          style={styles.contentContainer}>
          <View
            testID={tID('EditProfile-View-ProfilePicContainer')}
            style={styles.titleContainer}>
            <UserAvatar
              name={firstName + ' ' + lastName}
              size={120}
              source={
                profileImage && typeof profileImage != 'string'
                  ? profileImage.uri
                  : profileImage
              }
              radius={24}
            />
            <Text
              testID={tID('EditProfile-Text-UpdateAvatar')}
              variation="Body2"
              color={colors.link}
              topMargin={16}
              onPress={onPressUpdateAvtar}>
              Update Avatar
            </Text>
          </View>
          <View
            testID={tID('EditProfile-View-FormContainer')}
            style={styles.typeContainer}>
            <PersonalDetails
              firstName={firstName}
              lastName={lastName}
              gender={gender}
              dateOfBirth={dateOfBirth}
              diabetesTypes={diabetesTypes}
              otherDiabetesType={otherDiabetesType}
              timeFormat={timeFormat}
              firstNameError={firstNameError}
              lastNameError={lastNameError}
              otherTypeError={otherTypeError}
              setFirstName={setFirstName}
              setLastName={setLastName}
              setGender={setGender}
              setDateOfBirth={onDateOfBirthChange}
              onPressChangePassword={onPressChangePassword}
              setOtherDiabetesType={setOtherDiabetesType}
              setDiabetesTypes={setDiabetesTypes}
              setTimeFormat={setTimeFormat}>
              {children}
            </PersonalDetails>
            <ContactDetails
              phoneNo={phone}
              email={email}
              setEmail={setEmail}
              onPressChangePhone={onPressChangePhone}
            />
          </View>
          <View
            testID={tID('EditProfile-View-ButtonContainer')}
            style={[styles.buttonsContainer, {paddingBottom: insets.bottom + 24}]}>
            <Button
              testID={tID('EditProfile-Button-DeleteAccount')}
              style={styles.deleteButton}
              onPress={() => setShowDeletePopup(true)}
              title={'Delete Account'}
              buttonType={BUTTON_TYPES.Plain}
            />
            <Button
              testID={tID('EditProfile-Button-SaveChanges')}
              disabled={!isValidForm}
              onPress={updateProfile}
              title={'Save Changes'}
              buttonType={BUTTON_TYPES.Filled}
              titleColor={colors.textOnAccent}
            />
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
      <SafeAreaView />
      <ActionSheet
        ref={actionsheet}
        title={'Please Select'}
        options={['Pick from gallery', 'Capture using camera', 'Cancel']}
        cancelButtonIndex={2}
        onPress={onPressActionSheetOption}
        styles={
          Platform.OS === 'web' && {
            wrapper: styles.wrapper,
          }
        }
      />
      {renderDeleteAccountModal()}
      {renderDeleteAccountConfirmation()}
      <PermissionDeniedModal
        visible={cameraPermissionDenied}
        onRetry={onPermissionRetry}
        onClose={() => setCameraPermissionDenied(false)}
      />
      <PermissionBlockedModal
        visible={cameraPermissionBlocked}
        onOpen={onOpenSettings}
        onClose={() => setCameraPermissionBlocked(false)}
      />
    </View>
  );
};

export default EditProfile;
